import type { HttpError, HttpErrorOptions } from './errors';
import { UnsupportedRequestError } from './errors';
import { dropRejection } from './thenable';
import type {
  BeforeTrace,
  GlobalAfterRequestHandler,
  GlobalBeforeRequestHandler,
  ObservedResponse,
  Policy,
  RequestOptions,
  RetryConfig,
  Trace,
  Unregister,
} from './types';

/* ---------------------------------------------------------------------------
 * The handlers. Two lists per call, and two more that the whole process shares.
 * ------------------------------------------------------------------------- */

/** Registered with `request.before`. Runs AFTER the call's own list (BE6, GB4). */
const globalBefore: GlobalBeforeRequestHandler[] = [];

/** Registered with `request.after`. Runs AFTER the call's own list (AF18, GA11). */
const globalAfter: GlobalAfterRequestHandler[] = [];

/**
 * Adds a handler to a process-wide list and hands back the way to remove it.
 *
 * GB8 is what the return value is for: a registration used to last as long as the
 * process, so in a test one leaked into the next.
 *
 * The remover is idempotent, which is what lets a teardown unregister without
 * first checking. Searching the list cannot carry that on its own: two plugins
 * importing ONE shared handler put the same reference in twice, and a second call
 * then finds the OTHER registration instead of -1 and removes a handler this
 * teardown never added. The flag makes "already removed" a fact about this
 * remover rather than a question about the list.
 *
 * The `indexOf` guard is belt and braces behind it. Every remover fires once and
 * every live remover has an entry of its own, so nothing reaches it today.
 * MEASURED: removing it leaves the suite green. It stays because what it stops is
 * a splice at -1, which takes the LAST entry -- somebody else's handler -- and
 * that is one line of code away for whoever gives this list a second way to
 * shrink.
 */
function register<H>(list: H[], handler: H): Unregister {
  list.push(handler);
  let removed = false;
  return () => {
    if (removed) return;
    removed = true;
    const index = list.indexOf(handler);
    if (index !== -1) list.splice(index, 1);
  };
}

/** Registers a handler that runs before every call in the process. */
export function registerGlobalBefore(
  handler: GlobalBeforeRequestHandler,
): Unregister {
  return register(globalBefore, handler);
}

/** Registers an observer that runs after every call in the process. */
export function registerGlobalAfter(
  handler: GlobalAfterRequestHandler,
): Unregister {
  return register(globalAfter, handler);
}

/**
 * One handler or a list of them, as one list.
 *
 * BE32 and AF21 are why both forms exist. BE33 needs no branch of its own: not
 * set and an empty list both arrive here as zero handlers, so "nothing runs" is
 * the same code path rather than the same promise made twice.
 */
function asList<H>(value: H | H[] | undefined): H[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Runs a `before` list and hands back what it made of the options.
 *
 * BE2: a handler that returns options replaces them, and the next one reads that
 * result. BE3: one that returns nothing leaves them as they were. BE4: the result
 * is awaited, which costs the call nothing it was not already paying — these ARE
 * the options the call goes out with, so there is nothing to get on with until
 * they are known.
 *
 * BE7: nothing is caught. A `before` handler that throws is refusing the call,
 * and that refusal belongs to the caller.
 */
async function chain<O>(
  options: O,
  handlers: readonly ((
    options: O,
    signal: AbortSignal,
    trace: BeforeTrace,
  ) => O | void | Promise<O | void>)[],
  signal: AbortSignal,
  trace: BeforeTrace,
): Promise<O> {
  let current = options;
  for (const handler of handlers) {
    // Sequential on purpose: BE2 makes each handler's input the previous one's
    // output, so there is nothing here to run in parallel.
    //
    // SI27: no `if (signal.aborted) break` here, and that is decided rather than
    // forgotten. Skipping the rest would be a second, invisible behavior on top
    // of the one this parameter adds, and a handler may want to compensate for
    // what its predecessor left half-done. It is told the truth instead, through
    // the signal it is handed, and decides for itself.
    const next = await handler(current, signal, trace);
    if (next !== undefined) current = next;
  }
  return current;
}

/**
 * The per-call `before` list, run against the variant the caller actually chose.
 *
 * The six branches are DECISION 3.9 being paid for rather than decorated around.
 * Every variant has its OWN handler type, so a `RawHeadBeforeRequestHandler` only
 * becomes callable once the compiler knows this call is a raw HEAD; handing it
 * the four-member union instead is exactly the hole that split exists to close.
 *
 * MEASURED, narrowing less does not compile: on `raw` and `context` alone the
 * compiler still reports `Argument of type 'RequestOptions' is not assignable to
 * parameter of type 'RawNonHeadRequestOptions'`, because the HEAD axis is what
 * separates those last two.
 *
 * And they stay. This is a CORRELATED union: once widened, the compiler sees a
 * union of options beside a union of handlers and has lost which goes with
 * which, so `chain(rawHeadOptions, [decodedHandler])` would become legal. The
 * six branches ARE that proof. Any signature that accepts the widened pair hides
 * the unsafety behind an assertion or bivariance.
 *
 * Consequence worth knowing before the next sweep: mutation testing reports some
 * thirty survivors here that no test can ever kill, because every branch runs
 * identical code. That is the price of the proof, not a gap (BE37).
 */
function runCallBefore(
  options: RequestOptions,
  signal: AbortSignal,
  trace: BeforeTrace,
): Promise<RequestOptions> {
  if (options.raw !== true) {
    if ('context' in options) {
      return chain(options, asList(options.before), signal, trace);
    }
    return chain(options, asList(options.before), signal, trace);
  }
  if (options.method === 'HEAD') {
    if ('context' in options) {
      return chain(options, asList(options.before), signal, trace);
    }
    return chain(options, asList(options.before), signal, trace);
  }
  if ('context' in options) {
    return chain(options, asList(options.before), signal, trace);
  }
  return chain(options, asList(options.before), signal, trace);
}

/**
 * The call's own `before` handlers, then the process-wide ones.
 *
 * BE6 and GB4: the global list runs LAST, so a globally registered handler has
 * the final word where both write the same header. The copy is not about
 * concurrency — a handler may unregister itself while the list is being walked,
 * and splicing the array under the loop would skip its neighbour.
 *
 * IS5 and IS17: `isolated` drops the process-wide list only. The call's own
 * handlers are not process-wide, so they run either way.
 *
 * SI28: both lists are handed the SAME signal. A globally registered handler is
 * preparing the same call, so there is one contract here rather than two.
 */
export async function runBefore(
  options: RequestOptions,
  isolated: boolean,
  signal: AbortSignal,
  trace: BeforeTrace,
): Promise<{ final: RequestOptions; own: RequestOptions }> {
  const own = await runCallBefore(options, signal, trace);
  // Both halves come back, because the refusal below has to tell them apart:
  // what the caller's own handlers left is the baseline the global list may not
  // move (`GB16`).
  const final = isolated
    ? own
    : await chain(own, [...globalBefore], signal, trace);
  return { final, own };
}

/**
 * The options the pipeline has already read by the time the first `before`
 * handler runs.
 *
 * All of them live on the shared base of `RequestOptions`, so a key indexes the
 * whole four-variant union without a narrow and without an assertion.
 *
 * `signal` and `isolate` were missing and behaved worse for it. Being read early
 * is what puts a name on this list, and both are: the deadline is armed with the
 * caller's signal before any handler runs, and `isolate` decides which handlers
 * are in scope at all, so it is read before there is a scope to speak of. A
 * handler setting either therefore changed nothing, and because they were not on
 * this list it changed nothing SILENTLY -- while `timeout`, read at the very same
 * moment, was refused.
 *
 * `isolate` is the one that cost something. It exists for a token that differs
 * per user, a handler is where such a token is added, and `{ ...o, isolate: true }`
 * there reads as the careful move while buying nothing.
 *
 * The list is hand-written and nothing holds it against the code, which is the
 * shape that let two names go missing. Adding a name here is cheap; noticing that
 * one is absent is not.
 */
const FIXED_BEFORE_THE_HANDLERS_RUN = [
  'isolate',
  'policies',
  'retry',
  'signal',
  'timeout',
] as const;

/**
 * The two a GLOBAL handler may not touch, where an own handler may (`GB16`).
 *
 * The difference is trust rather than mechanism. A caller who passes their own
 * handlers knows them; a globally registered one belongs to another plugin. And
 * the mistake needs no bad intent: `{ ...o, after: [metrics] }` reads as adding
 * and is replacing, which stops the caller's own logging with no error at all.
 */
const OWNED_BY_THE_CALLER = ['after', 'before'] as const;

/**
 * Every field of `RetryConfig`, so the comparison below reads the whole config
 * rather than the two fields that happened to matter when it was written.
 *
 * `satisfies` is what keeps it honest in the one direction that has bitten this
 * package: a field REMOVED from `RetryConfig` and left here stops compiling. A
 * field ADDED there is not caught, which is the residual and is why the list
 * sits beside the type it mirrors.
 */
const RETRY_FIELDS = [
  'backoff',
  'maxAttempts',
  'networkErrors',
  'onAttempt',
  'onRetry',
  'perAttemptTimeoutMs',
  'retryableStatuses',
  'shouldRetry',
] as const satisfies readonly (keyof RetryConfig)[];

/**
 * Every field of `RetryConfig` appears in the list above, and this line is what
 * keeps that true.
 *
 * `satisfies` checks that each name in the list EXISTS on the type. It says
 * nothing about a name that is MISSING, and a missing name is the whole failure
 * mode: a field added to `RetryConfig` later would simply not be compared, and a
 * handler mutating it would walk through the guard unseen. This alias is `never`
 * while the list is complete, and naming the uncovered field is what the
 * compiler prints when it is not.
 */
type MustBeNever<T extends never> = T;
export type UncoveredRetryField = MustBeNever<
  Exclude<keyof RetryConfig, (typeof RETRY_FIELDS)[number]>
>;

/** What the `retry` position holds: our loop's config, the caller's own, or neither. */
export type RetryOption = RetryConfig | Policy | undefined;

/**
 * Whether the caller's own `retry` object still holds what was copied from it
 * before the first handler ran.
 *
 * A `Policy` and an absent `retry` answer no: a function has no fields to move,
 * and the identity check covers a handler that swaps one for another.
 */
function retryChangedInPlace(atEntry: RetryOption, now: RetryOption): boolean {
  if (atEntry === undefined || typeof atEntry === 'function') return false;
  if (now === undefined || typeof now === 'function') return false;
  return RETRY_FIELDS.some(field => !Object.is(atEntry[field], now[field]));
}

/**
 * Whether the handlers changed the CONTENTS of the policy list.
 *
 * The identity check in `refuseHandlerRewrites` already refuses a handler that
 * hands back a NEW array. This is the other half, and `BE43` is the precedent: a
 * handler that pushes into the caller's own array keeps the reference, so that
 * check sees nothing -- while the push reaches nothing either, because `guarded`
 * composed the stack before the first handler ran. A write that changes nothing
 * is exactly the silent no-op these refusals exist to turn into an answer
 * (BE47).
 *
 * Per index rather than by length alone, which covers reordering and removal as
 * well as the push it is named for. MEASURED: reorder, remove and clear all
 * answer true, while a push followed by the matching pop answers false -- and
 * that is right, because nothing is left to refuse.
 */
function policiesChangedInPlace(
  atEntry: readonly Policy[] | undefined,
  now: readonly Policy[] | undefined,
): boolean {
  if (atEntry === undefined || now === undefined) return false;
  if (atEntry.length !== now.length) return true;
  return atEntry.some((policy, index) => !Object.is(policy, now[index]));
}

/** The one sentence both refusals below are made of. */
function alreadyRead(field: string): string {
  return `a \`before\` handler cannot change \`${field}\`, because the pipeline has already read it`;
}

/*
 * The two options that are compared by CONTENTS rather than by identity, as
 * they stood before the first handler ran.
 *
 * One parameter rather than two, because they are one idea: a shallow copy of
 * what a handler could write INTO without the identity check noticing. A third
 * such option joins this object rather than the argument list.
 *
 * A plain block and not a doc block: the doc block for `refuseHandlerRewrites`
 * lives directly above that function, and two stacked doc blocks mean only the
 * lower one binds (PF24).
 */
interface BeforeTheHandlers {
  readonly allowEmptyResponse: boolean | undefined;
  readonly methodWasHead: boolean;
  readonly policies: readonly Policy[] | undefined;
  readonly raw: boolean | undefined;
  readonly retry: RetryOption;
  readonly throwOnError: RequestOptions['throwOnError'];
}

/** Preserve the result-shape switches before any handler can rewrite options. */
export function beforeStateOf(
  options: RequestOptions,
  policies: readonly Policy[] | undefined,
  retry: RetryOption,
): BeforeTheHandlers {
  return {
    allowEmptyResponse: options.allowEmptyResponse,
    methodWasHead: options.method === 'HEAD',
    policies,
    raw: options.raw,
    retry,
    throwOnError: options.throwOnError,
  };
}

/**
 * Refuses a `before` handler that rewrote an option it may not change, naming
 * the one it rewrote (BE38).
 *
 * Refused: a change to `allowEmptyResponse`, `raw` or `throwOnError`, which
 * chose the result type at the call site; turning a raw call to or from HEAD;
 * a change to `isolate`, `policies`, `retry`, `signal` or `timeout`, which the
 * pipeline has already read; and, from a GLOBAL handler, a change to `before`
 * or `after`, which belong to the caller.
 *
 * `retry`, `policies` and `timeout` are read BEFORE the first handler runs: `guarded` composes `retry`
 * and `policies` into the stack that wraps the attempts, and `startDeadline`
 * arms the clock off `timeout`. A handler that rewrites one of them therefore
 * changes NOTHING, and until BE38 it changed nothing SILENTLY, while an
 * out-of-range value was still rejected. The library turned down a value it then
 * threw away.
 *
 * Refusing beats restarting the clock, which repairs `timeout` alone, leaves the
 * other two just as silent, and lets a slow handler stretch the caller's own
 * budget. That last one is precisely the call-to-answer property TO28 pins down.
 *
 * Compared by IDENTITY, which is what the spread a handler writes already
 * preserves: `{ ...options, headers }` hands back the same `retry` object and
 * the same `policies` array, so the ordinary handler passes untouched. Only a
 * deliberate rewrite changes the reference, and a rewrite is exactly what cannot
 * be honoured.
 *
 * IDENTITY IS ONLY HALF OF IT, and `retryAtEntry` is the other half (BE43). A
 * handler that writes INTO the caller's own `retry` object keeps the reference,
 * so the loop above sees nothing while the write reaches nothing either -- the
 * attempts run on the copy taken before the handlers started. MEASURED with a
 * control arm: three attempts on the wire without the handler, one with it. A
 * write that changes nothing is precisely the silent no-op this whole function
 * exists to turn into a refusal, so the copy is compared field by field.
 *
 * The method is READ OFF `final` rather than handed in. It is the one the call
 * goes out with, which is what every refusal below has to name, and taking it
 * from the same object the rest of the function judges leaves no second source
 * for a caller to pass the wrong one from.
 */
export function refuseHandlerRewrites(
  given: RequestOptions,
  own: RequestOptions,
  final: RequestOptions,
  made: HttpErrorOptions,
  atEntry: BeforeTheHandlers,
): void {
  const method = final.method ?? 'GET';
  const defaults = {
    allowEmptyResponse: false,
    raw: false,
    throwOnError: true,
  } as const;
  for (const field of ['allowEmptyResponse', 'raw', 'throwOnError'] as const) {
    if (
      Object.is(
        atEntry[field] ?? defaults[field],
        final[field] ?? defaults[field],
      )
    )
      continue;
    throw new UnsupportedRequestError(method, final.url, {
      ...made,
      detail: `a \`before\` handler cannot change \`${field}\`, because the result type was chosen at the call site`,
    });
  }
  if (atEntry.raw === true && atEntry.methodWasHead !== (method === 'HEAD')) {
    throw new UnsupportedRequestError(method, final.url, {
      ...made,
      detail:
        'a `before` handler cannot change a raw call to or from HEAD, because the result stream shape was chosen at the call site',
    });
  }
  for (const field of FIXED_BEFORE_THE_HANDLERS_RUN) {
    // `Object.is` and not `===`, for the one value that is not equal to itself:
    // a `timeout` of `NaN` reported a rewrite where the handler had left the
    // caller's number exactly as it found it, and named the wrong reason for a
    // call that is refused anyway.
    if (Object.is(given[field], final[field])) continue;
    throw new UnsupportedRequestError(method, final.url, {
      ...made,
      detail: alreadyRead(field),
    });
  }
  if (retryChangedInPlace(atEntry.retry, final.retry)) {
    throw new UnsupportedRequestError(method, final.url, {
      ...made,
      detail: alreadyRead('retry'),
    });
  }
  if (policiesChangedInPlace(atEntry.policies, final.policies)) {
    throw new UnsupportedRequestError(method, final.url, {
      ...made,
      detail: alreadyRead('policies'),
    });
  }
  for (const field of OWNED_BY_THE_CALLER) {
    if (own[field] === final[field]) continue;
    throw new UnsupportedRequestError(method, final.url, {
      ...made,
      detail: `a GLOBAL \`before\` handler cannot change \`${field}\`: that list belongs to the caller, and replacing it would silently drop their own handlers`,
    });
  }
}

/** What an observer is handed, before the variant pins `options` down. */
type ObservedCall = Parameters<GlobalAfterRequestHandler>[0];

/** Everything an observer sees except the options: AF5 minus the one that varies. */
export interface Outcome {
  body: unknown;
  /**
   * What ended the call, classified or not, and read by the debug line alone.
   *
   * `error` is only ever a failure this library produced, which is what makes it
   * safe for the `after` contract. A throw from the caller's own code belongs in
   * neither that field nor that promise, and leaving the log with nothing made
   * the one line a call writes report `undefined` for what went wrong (DB17).
   */
  endedWith?: unknown;
  error?: HttpError;
  response?: ObservedResponse;
}

/**
 * Hands one outcome to a list of observers.
 *
 * AF3 and AF22: whatever a handler returns goes nowhere. AF4: nothing is awaited,
 * so a slow logger stays off the caller's critical path. AF7: a throw is caught
 * and dropped, because an observer that can fail the call is not an observer.
 *
 * "Goes nowhere" is the reason the return value is handed to `dropRejection`
 * rather than ignored. The handler type says `void`, which an `async` handler
 * satisfies, and that one rejects rather than throws: the `catch` never sees it
 * and the process, not the call, is what ends.
 */
export function observe<A>(
  handlers: readonly ((args: A) => void)[],
  args: A,
): void {
  for (const handler of handlers) {
    try {
      dropRejection(handler(args));
    } catch {
      // Dropped on purpose: AF7 and GA8. See above.
    }
  }
}

/**
 * The per-call observers first, then the process-wide ones, on ONE args object.
 *
 * AF18 and GA11 fix that order and GA4 gives the reason: a global observer may
 * lean on what a per-call one did — the span it opened, the field it set — which
 * only works if it runs afterwards.
 *
 * The constraint is what lets both lists read the same object. A per-call
 * observer is promised the narrow options variant and a global one the union, and
 * an args object built for the narrow one satisfies both.
 *
 * IS5 and IS18: `isolated` drops the process-wide list only, for the reason
 * spelled out on `runBefore`.
 */
function deliver<A extends ObservedCall>(
  args: A,
  handlers: readonly ((args: A) => void)[],
  isolated: boolean,
): void {
  // Taken BEFORE either list runs, which is the whole point: copying afterwards
  // copies what the first list already wrote, so the protection would hold in
  // one direction only -- and which direction that is depends on registration
  // order, which neither list can see (GB19).
  //
  // `Notify` says what these handlers are for: "it announces and does not look
  // back", and "an observer cannot change the outcome of the call". Writing into
  // the args object was the one way to reach another handler anyway. A caller
  // who wants handlers to talk has `context`, which they own and pass in, and
  // which reaches `before`, the policies and both `after` lists.
  //
  // SHALLOW: this separates the two top-level objects, not what a nested object
  // may do. Copying deeply would cost every call and cannot work for a
  // `response`, and `context` is deliberately shared.
  const forGlobal: ObservedCall = isolated ? args : { ...args };
  observe(handlers, args);
  if (isolated) return;
  observe<ObservedCall>([...globalAfter], forGlobal);
}

/**
 * Runs the `after` lists against the variant the call turned out to be.
 *
 * Six branches for the reason spelled out on `runCallBefore`. The raw half also
 * differs in substance rather than only in types: those observers are promised
 * `body: undefined`, and a call that decoded nothing is exactly what produced it.
 */
export function runAfter(
  options: RequestOptions,
  outcome: Outcome,
  isolated: boolean,
  trace: Trace,
): void {
  if (options.raw !== true) {
    if ('context' in options) {
      deliver({ ...outcome, options, trace }, asList(options.after), isolated);
      return;
    }
    deliver({ ...outcome, options, trace }, asList(options.after), isolated);
    return;
  }
  const rawOutcome = { ...outcome, body: undefined };
  if (options.method === 'HEAD') {
    if ('context' in options) {
      deliver(
        { ...rawOutcome, options, trace },
        asList(options.after),
        isolated,
      );
      return;
    }
    deliver({ ...rawOutcome, options, trace }, asList(options.after), isolated);
    return;
  }
  if ('context' in options) {
    deliver({ ...rawOutcome, options, trace }, asList(options.after), isolated);
    return;
  }
  deliver({ ...rawOutcome, options, trace }, asList(options.after), isolated);
}
